import React, { useCallback, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import colors from 'utils/colors';
import { useInputState } from 'utils/hooks';
import { WaveStats } from 'utils/simulate';
import { useMaxWave } from 'contexts/gameState';
import { useResults } from 'contexts/results';
import { TIERS, TIER_MULTIPLIERS } from './ResultsTable';
import './graphs.scss';

const BILLION = 1_000_000_000;
const MILLION = 1_000_000;
const HOUR = 60 * 60;

const axisProps = {
  axisLine: { stroke: colors.secondary },
  tickLine: { stroke: colors.secondary },
  tick: { stroke: colors.secondary },
};

const waveLabel = label => `Wave ${label}`;
const labelStyle = { color: colors.secondary };

const ceil = (value: string) => Math.ceil(parseFloat(value));

const Chart: React.FC<{
  waveStats: [string, WaveStats][];
  statFunction: (
    WaveStats: WaveStats,
    wave: number,
    previous?: WaveStats,
    previousWave?: number,
  ) => number | string;
  label: string;
  domain: [number, number];
}> = ({ waveStats, statFunction, label, domain }) => (
  <div className='chart'>
    <div className='title'>{label}</div>
    <BarChart
      width={600}
      height={400}
      data={waveStats.map(([wave, stat], i) => ({
        name: wave,
        value: statFunction(
          stat,
          parseInt(wave, 10),
          waveStats[i - 1]?.[1],
          parseInt(waveStats[i - 1]?.[0] || '0', 10),
        ),
      }))}
    >
      <XAxis dataKey='name' {...axisProps} />
      <YAxis domain={domain} {...axisProps} />
      <Tooltip labelStyle={labelStyle} labelFormatter={waveLabel} />
      <Bar dataKey='value' fill={colors.primary} />
    </BarChart>
  </div>
);

const Graphs: React.FC<{}> = () => {
  const results = useResults();
  const maxWave = useMaxWave();

  const waveStats = useMemo(
    () => Object.entries(results.stats.waves),
    [results.stats.waves],
  );
  const [tier, setTier] = useInputState(8, 'graphTier');
  const multiplier = TIER_MULTIPLIERS[tier];
  const max = results.stats.waves[maxWave];
  const beforeMaxWave = Math.floor((maxWave - 1) / 100) * 100;
  const beforeMax = results.stats.waves[beforeMaxWave];

  const percentTotal = useCallback(
    (stat: WaveStats) => ((stat.coins * 100) / (max?.coins || 1)).toFixed(2),
    [max],
  );
  const totalCoins = useCallback(
    (stat: WaveStats) => ((stat.coins * multiplier) / BILLION).toFixed(2),
    [multiplier],
  );
  const cph = useCallback(
    (stat: WaveStats) => ((stat.cph * multiplier) / BILLION).toFixed(2),
    [multiplier],
  );
  const cpw = useCallback(
    (stat: WaveStats, wave: number) =>
      ((stat.coins * multiplier) / MILLION / wave).toFixed(2),
    [multiplier],
  );

  const percentTotalWindow = useCallback(
    (stat: WaveStats, _wave, previous?: WaveStats) =>
      (
        ((stat.coins - (previous?.coins || 0)) * 100) /
        (max?.coins || 1)
      ).toFixed(2),
    [max],
  );
  const totalCoinsWindow = useCallback(
    (stat: WaveStats, _wave, previous?: WaveStats) =>
      (((stat.coins - (previous?.coins || 0)) * multiplier) / MILLION).toFixed(
        2,
      ),
    [multiplier],
  );
  const cphWindow = useCallback(
    (stat: WaveStats, _wave, previous?: WaveStats) =>
      (
        (((stat.coins - (previous?.coins || 0)) /
          ((stat.time - (previous?.time || 0)) / HOUR)) *
          multiplier) /
        BILLION
      ).toFixed(2),
    [multiplier],
  );
  const cpwWindow = useCallback(
    (
      stat: WaveStats,
      wave: number,
      previous?: WaveStats,
      previousWave?: number,
    ) =>
      (
        ((stat.coins - (previous?.coins || 0)) * multiplier) /
        MILLION /
        (wave - (previousWave || 0))
      ).toFixed(2),
    [multiplier],
  );

  const perkMultiplier = useCallback(
    (stat: WaveStats) => stat.perkMultiplier.toFixed(4),
    [],
  );
  const waveSkips = useCallback(
    (stat: WaveStats) => stat.waveSkips.toFixed(2),
    [],
  );
  const basicCoinBonuses = useCallback(
    (stat: WaveStats) => stat.basicCoinBonuses.toFixed(2),
    [],
  );
  const defenseBonuses = useCallback(
    (stat: WaveStats) => stat.defenseBonuses.toFixed(2),
    [],
  );
  const extraOrbs = useCallback(
    (stat: WaveStats) => stat.extraOrbs.toFixed(2),
    [],
  );
  const hpBonuses = useCallback(
    (stat: WaveStats) => stat.hpBonuses.toFixed(2),
    [],
  );
  const perkCount = useCallback(
    (stat: WaveStats) => stat.perkCount.toFixed(2),
    [],
  );
  const hasGoldenTower = useCallback(
    (stat: WaveStats) => (stat.hasGoldenTower * 100).toFixed(2),
    [],
  );
  const hasTradeoffCoins = useCallback(
    (stat: WaveStats) => (stat.hasTradeoffCoins * 100).toFixed(2),
    [],
  );
  const hasDamageTradeoff = useCallback(
    (stat: WaveStats) => (stat.hasDamageTradeoff * 100).toFixed(2),
    [],
  );
  const noMoreOptions = useCallback(
    (stat: WaveStats) => (stat.noMoreOptions * 100).toFixed(2),
    [],
  );
  const hasGameSpeed = useCallback(
    (stat: WaveStats) => (stat.hasGameSpeed * 100).toFixed(2),
    [],
  );
  const hasAllCoinBonuses = useCallback(
    (stat: WaveStats) => (stat.hasAllCoinBonuses * 100).toFixed(2),
    [],
  );
  const hasAllNeededPerks = useCallback(
    (stat: WaveStats) => (stat.hasAllNeededPerks * 100).toFixed(2),
    [],
  );
  const hasFirstPerkReduction = useCallback(
    (stat: WaveStats) => (stat.hasFirstPerkReduction * 100).toFixed(2),
    [],
  );
  const hasSecondPerkReduction = useCallback(
    (stat: WaveStats) => (stat.hasSecondPerkReduction * 100).toFixed(2),
    [],
  );
  const hasThirdPerkReduction = useCallback(
    (stat: WaveStats) => (stat.hasThirdPerkReduction * 100).toFixed(2),
    [],
  );

  if (!waveStats.length) return <div id='graphs'>Run some simulations</div>;
  return (
    <div id='graphs'>
      <div className='controls'>
        Show for tier
        <select value={tier} onChange={setTier}>
          {TIERS.map(t => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      <div className='charts'>
        <Chart
          waveStats={waveStats}
          label='Percent of total coins'
          domain={[0, 100]}
          statFunction={percentTotal}
        />
        <Chart
          waveStats={waveStats}
          label='Total coins (B)'
          domain={[0, ceil(totalCoins(max))]}
          statFunction={totalCoins}
        />
        <Chart
          waveStats={waveStats}
          label='CPH (B)'
          domain={[0, ceil(cph(max))]}
          statFunction={cph}
        />
        <Chart
          waveStats={waveStats}
          label='CPW (M)'
          domain={[0, ceil(cpw(max, maxWave))]}
          statFunction={cpw}
        />
        <Chart
          waveStats={waveStats}
          label='Percent of total coins since last 100'
          domain={[0, 100]}
          statFunction={percentTotalWindow}
        />
        <Chart
          waveStats={waveStats}
          label='Total coins since last 100 (M)'
          domain={[0, ceil(totalCoinsWindow(max, maxWave, beforeMax))]}
          statFunction={totalCoinsWindow}
        />
        <Chart
          waveStats={waveStats}
          label='CPH since last 100 (B)'
          domain={[0, ceil(cphWindow(max, maxWave, beforeMax))]}
          statFunction={cphWindow}
        />
        <Chart
          waveStats={waveStats}
          label='CPW since last 100 (M)'
          domain={[0, ceil(cpwWindow(max, maxWave, beforeMax, beforeMaxWave))]}
          statFunction={cpwWindow}
        />
        <Chart
          waveStats={waveStats}
          label='Multiplier'
          domain={[1, ceil(perkMultiplier(max))]}
          statFunction={perkMultiplier}
        />
        <Chart
          waveStats={waveStats}
          label='Basic Coin Bonuses'
          domain={[0, 5]}
          statFunction={basicCoinBonuses}
        />
        <Chart
          waveStats={waveStats}
          label='Defense Bonuses'
          domain={[0, 5]}
          statFunction={defenseBonuses}
        />
        <Chart
          waveStats={waveStats}
          label='HP Bonuses'
          domain={[0, 5]}
          statFunction={hpBonuses}
        />
        <Chart
          waveStats={waveStats}
          label='Extra Orbs'
          domain={[0, 2]}
          statFunction={extraOrbs}
        />
        <Chart
          waveStats={waveStats}
          label='Percent With Game Speed Bonus'
          domain={[0, 100]}
          statFunction={hasGameSpeed}
        />
        <Chart
          waveStats={waveStats}
          label='Percent With First Perk Reduction'
          domain={[0, 100]}
          statFunction={hasFirstPerkReduction}
        />
        <Chart
          waveStats={waveStats}
          label='Percent With Second Perk Reduction'
          domain={[0, 100]}
          statFunction={hasSecondPerkReduction}
        />
        <Chart
          waveStats={waveStats}
          label='Percent With Third Perk Reduction'
          domain={[0, 100]}
          statFunction={hasThirdPerkReduction}
        />
        <Chart
          waveStats={waveStats}
          label='Percent With Golden Tower Bonus'
          domain={[0, 100]}
          statFunction={hasGoldenTower}
        />
        <Chart
          waveStats={waveStats}
          label='Percent With Coin Tradeoff'
          domain={[0, 100]}
          statFunction={hasTradeoffCoins}
        />
        <Chart
          waveStats={waveStats}
          label='Percent With Damage Tradeoff'
          domain={[0, 100]}
          statFunction={hasDamageTradeoff}
        />
        <Chart
          waveStats={waveStats}
          label='Percent With All Coin Bonuses'
          domain={[0, 100]}
          statFunction={hasAllCoinBonuses}
        />
        <Chart
          waveStats={waveStats}
          label='Percent With All Needed Perks'
          domain={[0, 100]}
          statFunction={hasAllNeededPerks}
        />
        <Chart
          waveStats={waveStats}
          label='Percent With No More Options'
          domain={[0, 100]}
          statFunction={noMoreOptions}
        />
        <Chart
          waveStats={waveStats}
          label='Perk Count'
          domain={[0, ceil(perkCount(max))]}
          statFunction={perkCount}
        />
        <Chart
          waveStats={waveStats}
          label='Wave Skips'
          domain={[0, ceil(waveSkips(max))]}
          statFunction={waveSkips}
        />
      </div>
    </div>
  );
};

export default Graphs;
